import {useAuth} from '../auth/use-auth';
import {useSettings} from '@ui/settings/use-settings';
import dot from 'dot-object';
import {useMemo} from 'react';
import {User} from '@ui/types/user';
import {MenuConfig, MenuItemConfig} from '@common/menus/menu-config';

// AFLM content locale navigation.
// Stored menu actions stay canonical (English/unprefixed).
// Locale prefix is applied only when rendering public content links.
const AFLM_CONTENT_LOCALES = new Set([
  'ar',
  'fr',
  'de',
  'es',
  'it',
  'pt',
  'tr',
  'ru',
  'pl',
  'nl',
  'sv',
  'da',
  'fi',
  'cs',
  'el',
  'he',
  'fa',
  'ur',
  'hi',
  'bn',
  'id',
  'th',
  'vi',
  'ja',
  'ko',
  'zh',
  'uk',
  'ro',
  'hu',
  'bg',
]);

const AFLM_LOCALIZED_MENU_ACTIONS = new Set([
  '/movies',
  '/series',
  '/people',
  '/latest-news',
]);

function currentAflmContentLocale(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const segment = window.location.pathname
    .split('/')
    .filter(Boolean)[0];

  return segment && AFLM_CONTENT_LOCALES.has(segment)
    ? segment
    : null;
}

function localizeAflmMenuAction(action: string): string {
  const locale = currentAflmContentLocale();

  if (!locale || !AFLM_LOCALIZED_MENU_ACTIONS.has(action)) {
    return action;
  }

  return `/${locale}${action}`;
}


export function useCustomMenu(menuOrPosition?: string | MenuConfig) {
  const settings = useSettings();
  const {user, hasPermission} = useAuth();

  return useMemo(() => {
    if (!menuOrPosition) {
      return null;
    }

    const menu =
      typeof menuOrPosition === 'string'
        ? settings.menus?.find(s => s.positions?.includes(menuOrPosition))
        : menuOrPosition;
      const menuItems: MenuItemConfig[] = [];

      if (!menu) {
        return menu;
      }

      for (const item of menu.items) {
        const hasRoles = (item.roles || []).every(
          a => user?.roles.find(b => b.id === a),
        );

        const hasPermissions = (item.permissions || []).every(a =>
          hasPermission(a),
        );

        const hasSettings =
          !item.settings ||
          Object.entries(item.settings).every(([key, value]) => {
            return dot.pick(key, settings) == value;
          });

        if (
          !item.action ||
          !hasRoles ||
          !hasPermissions ||
          !hasSettings ||
          !subscriptionStatusMatches(item, user)
        ) {
          continue;
        }

        let action = item.action;

        if (user?.id) {
          action = action.replace(
            /{currentUser}/g,
            `${user.id}`,
          );
        }

        action = localizeAflmMenuAction(action);

        menuItems.push({
          ...item,
          action,
        });
      }

      return {
        ...menu,
        items: menuItems,
      };
  }, [hasPermission, settings, menuOrPosition, user]);
}

function subscriptionStatusMatches(
  item: MenuItemConfig,
  user?: User | null,
): boolean {
  if (!item.subscriptionStatus) {
    return true;
  }
  const hasActiveSubscription = !!user?.subscriptions?.find(sub => sub.active);
  if (item.subscriptionStatus === 'subscribed') {
    return hasActiveSubscription;
  }
  return !hasActiveSubscription;
}
